import { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Save, UserPlus, Trash2, Key, Filter, Mail, Send, Loader2 } from "lucide-react";

const MTBF_FILTER_OPTIONS = [
  { value: "all", label: "All Delays (default)", desc: "Count every failure toward MTBF" },
  { value: "lt1", label: "< 1 Menit", desc: "Only failures with delay under 1 minute" },
  { value: "gt1", label: "> 1 Menit", desc: "Only failures with delay above 1 minute" },
  { value: "gt3", label: "> 3 Menit", desc: "Only failures with delay above 3 minutes" },
];

const SEVERITY_SCALE = [
  { level: "Level 1", label: "Catastrophic", color: "#dc2626", desc: "Fatalitas dan/atau cedera berat multipel; kerusakan lingkungan mayor" },
  { level: "Level 2", label: "Critical", color: "#d97706", desc: "Satu fatalitas dan/atau cedera berat; kerusakan lingkungan signifikan" },
  { level: "Level 3", label: "Marginal", color: "#65a30d", desc: "Cedera ringan dan/atau ancaman signifikan terhadap lingkungan; kerusakan sistem yang berat" },
  { level: "Level 4", label: "Insignificant", color: "#16a34a", desc: "Kemungkinan cedera ringan; kerusakan sistem minor" },
];

export default function Settings() {
  const { isAdmin, user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({ email: "", password: "", name: "", role: "viewer" });
  const [pwChange, setPwChange] = useState({ email: "", new_password: "" });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [startDate, setStartDate] = useState(new Date(new Date().setDate(1)).toISOString().slice(0,10));
  const [endDate, setEndDate] = useState(new Date().toISOString().slice(0,10));

  useEffect(() => {
    (async () => {
      const s = await api.get("/settings"); setSettings(s.data);
      if (isAdmin) { const u = await api.get("/users"); setUsers(u.data); }
    })();
  }, [isAdmin]);

  const saveSettings = async (patch, silent = false) => {
    try {
      const body = { ...settings, ...(patch || {}) };
      const { data } = await api.put("/settings", body);
      setSettings(data);
      if (!silent) toast.success("Settings saved");
      // Broadcast so live dashboards can refresh instantly.
      window.dispatchEvent(new CustomEvent("trams:settings-changed", { detail: data }));
      return data;
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Save failed"); }
  };

  const changeMtbfFilter = async (value) => {
    setSettings(s => ({ ...s, mtbf_delay_filter: value }));
    await saveSettings({ mtbf_delay_filter: value }, false);
  };

  const updateSeverity = (idx, field, value) => {
    setSettings(s => {
      const scale = [...(s.severity_scale || [])];
      scale[idx] = { ...scale[idx], [field]: value };
      return { ...s, severity_scale: scale };
    });
  };

  const removeSeverity = (idx) => {
    const scale = settings.severity_scale || [];
    const label = scale[idx]?.label || "level ini";
    if (!window.confirm(`Hapus kategori severity "${label}"? Perubahan berlaku setelah klik Save Scale.`)) return;
    setSettings(s => ({ ...s, severity_scale: (s.severity_scale || []).filter((_, i) => i !== idx) }));
  };

  const reclassifyRecords = async () => {
    try {
      await saveSettings({}, true);  // save first
      const { data } = await api.post("/settings/reclassify");
      toast.success(`Reclassified ${data.reclassified} records`);
      window.dispatchEvent(new CustomEvent("trams:settings-changed"));
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Reclassify failed"); }
  };

  const createUser = async () => {
    try {
      await api.post("/users", newUser);
      const u = await api.get("/users"); setUsers(u.data);
      setNewUser({ email: "", password: "", name: "", role: "viewer" });
      toast.success("User created");
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Create failed"); }
  };

  const delUser = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try {
      await api.delete(`/users/${id}`);
      const u = await api.get("/users"); setUsers(u.data);
      toast.success("Deleted");
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Delete failed"); }
  };

  const changePw = async () => {
    if (!pwChange.email || !pwChange.new_password) return;
    try {
      await api.post("/users/change_password", pwChange);
      setPwChange({ email: "", new_password: "" });
      toast.success("Password changed");
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Change failed"); }
  };

  const sendReportNow = async () => {
    if (!settings.email_distribution_list?.trim()) {
      toast.error("Tambahkan email di distribution list terlebih dulu");
      return;
    }
    setSendingEmail(true);
    try {
      const params = new URLSearchParams({ start: startDate, end: endDate }).toString();
      const { data } = await api.post(`/reports/email_now?${params}`);
      const ok = data.results.filter(r => r.ok).length;
      toast.success(`Email terkirim ke ${ok}/${data.recipients} penerima`);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Gagal mengirim email");
    } finally { setSendingEmail(false); }
  };

  if (!settings) return null;

  return (
    <div className="space-y-6 pb-8" data-testid="settings-page">
      {/* Severity Scale + MTBF Delay Filter (combined) */}
      <div className="bg-white p-6 rounded-xl border border-slate-200/60 kpi-shadow relative overflow-hidden">
        <div className="absolute top-0 left-0 w-1.5 h-full bg-gradient-to-b from-[#22c55e] via-[#eab308] to-[#ef4444]" />
        <div className="pl-3">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-[#f59e0b]" />
            <span className="text-[#93c5fd] font-black text-lg">2.</span>
            <h2 className="text-lg font-bold text-[#1e40af] uppercase tracking-tight">MTBF Delay Classification & Severity Scale</h2>
          </div>
          <p className="text-xs text-slate-500 mt-1 mb-4 ml-6">
            Skala severity kini berbasis <b>kategori konsekuensi</b> (Catastrophic / Critical / Marginal / Insignificant). Edit label, deskripsi & bobot (weight) tiap kategori. Klik tombol Reclassify Records untuk merapikan kategori tiap data. Data baru default <b>Insignificant</b> dan bisa diubah saat edit.
            MTBF filter di bawah membatasi kegagalan mana yang dihitung dalam rumus MTBF (real-time refresh).
          </p>

          {/* Editable Severity Scale */}
          <div className="mb-6 space-y-2">
            {(settings.severity_scale || []).map((s, i) => (
              <div key={s.level} className="grid grid-cols-12 gap-2 items-center p-2 rounded-lg border" style={{ borderColor: s.color + "44", background: s.color + "0d" }}>
                <div className="col-span-1 flex items-center justify-center">
                  <div className="w-3 h-3 rounded-full" style={{ background: s.color }} />
                </div>
                <div className="col-span-1 text-xs font-bold text-slate-900">Level {s.level}</div>
                <div className="col-span-3">
                  <Input value={s.label} onChange={e => updateSeverity(i, "label", e.target.value)}
                    className="h-8 text-xs" data-testid={`sev-label-${i}`} />
                </div>
                <div className="col-span-5">
                  <Input value={s.description} onChange={e => updateSeverity(i, "description", e.target.value)}
                    className="h-8 text-xs" data-testid={`sev-desc-${i}`} />
                </div>
                <div className="col-span-1">
                  <Input type="number" value={s.weight} step="0.1" min={0}
                    onChange={e => updateSeverity(i, "weight", Number(e.target.value))}
                    className="h-8 text-xs" placeholder="wt" title="Safety weight" data-testid={`sev-weight-${i}`} />
                </div>
                <div className="col-span-1 flex items-center justify-center">
                  <button
                    type="button"
                    onClick={() => removeSeverity(i)}
                    className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    title={(settings.severity_scale || []).length <= 1 ? "Minimal 1 kategori" : "Hapus kategori ini"}
                    disabled={(settings.severity_scale || []).length <= 1}
                    data-testid={`sev-delete-${i}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
            <div className="grid grid-cols-12 gap-2 text-[9px] uppercase tracking-widest text-slate-400 font-semibold px-2">
              <div className="col-span-1 text-center">Color</div>
              <div className="col-span-1">Level</div>
              <div className="col-span-3">Kategori</div>
              <div className="col-span-5">Konsekuensi</div>
              <div className="col-span-1">Weight</div>
              <div className="col-span-1 text-center">Aksi</div>
            </div>
          </div>

          <div className="flex gap-2 mb-6">
            <Button variant="outline" onClick={() => saveSettings()} data-testid="severity-save">
              <Save className="w-3.5 h-3.5 mr-1.5" /> Save Scale
            </Button>
            <Button className="bg-[#f59e0b] hover:bg-[#d97706]" onClick={reclassifyRecords} data-testid="severity-reclassify">
              <Filter className="w-3.5 h-3.5 mr-1.5" /> Reclassify All Records
            </Button>
          </div>

          <div className="border-t border-slate-200 pt-4 mt-2">
            <div className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-3">MTBF Delay Filter (real-time)</div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              {MTBF_FILTER_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => changeMtbfFilter(opt.value)}
                  data-testid={`mtbf-filter-${opt.value}`}
                  className={`text-left p-4 rounded-xl border-2 transition-all ${
                    settings.mtbf_delay_filter === opt.value
                      ? "border-[#1e40af] bg-blue-50/60 shadow"
                      : "border-slate-200 bg-white hover:border-[#93c5fd]"
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-sm font-bold text-slate-900">{opt.label}</div>
                    {settings.mtbf_delay_filter === opt.value && (<div className="w-2 h-2 rounded-full bg-[#22c55e]" />)}
                  </div>
                  <div className="text-[11px] text-slate-500 leading-relaxed">{opt.desc}</div>
                </button>
              ))}
            </div>
            <div className="mt-3 text-[11px] text-slate-500 flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-[#22c55e] animate-pulse" />
              Filter tersimpan otomatis. MTBF dan trend chart update real-time.
            </div>
          </div>
        </div>
      </div>

      {/* Email Report */}
      <div className="bg-white p-6 rounded-xl border border-slate-200/60 kpi-shadow relative overflow-hidden">
        <div className="absolute top-0 left-0 w-1.5 h-full bg-gradient-to-b from-[#3b82f6] to-[#1e40af]" />
        <div className="pl-3">
          <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-[#1e40af]" /><span className="text-[#93c5fd] font-black text-lg">1.</span><h2 className="text-lg font-bold text-[#1e40af] uppercase tracking-tight">Email Report Delivery</h2></div>
          <p className="text-xs text-slate-500 mt-1 mb-4 ml-6">Distribution list (pisahkan dengan koma). Klik tombol Send Now untuk mengirim PDF report periode terpilih segera.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Recipients</Label>
              <Input value={settings.email_distribution_list || ""}
                onChange={(e) => setSettings({...settings, email_distribution_list: e.target.value})}
                placeholder="ops@company.com, manager@company.com"
                className="mt-1.5" data-testid="settings-email-list" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Period</Label>
              <div className="grid grid-cols-2 gap-2 mt-1.5">
                <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
                <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
              </div>
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <Button variant="outline" onClick={() => saveSettings()} data-testid="settings-email-save"><Save className="w-3.5 h-3.5 mr-1.5" /> Save List</Button>
            <Button className="bg-[#1e40af] hover:bg-[#1e3a8a]" onClick={sendReportNow} disabled={sendingEmail} data-testid="settings-email-send">
              {sendingEmail ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
              {sendingEmail ? "Sending…" : "Send PDF Report Now"}
            </Button>
          </div>
        </div>
      </div>

      {/* MTBF filter card removed - merged into Severity Scale card above */}

      <div className="bg-white p-6 rounded-xl border border-slate-200/60 kpi-shadow relative overflow-hidden">
        <div className="absolute top-0 left-0 w-1.5 h-full bg-gradient-to-b from-[#1e40af] to-[#3b82f6]" />
        <div className="pl-3">
          <div className="flex items-center gap-2">
            <span className="text-[#93c5fd] font-black text-lg">2.</span>
            <h2 className="text-lg font-bold text-[#1e40af] uppercase tracking-tight">Operation Parameters</h2>
          </div>
          <p className="text-xs text-slate-500 mt-1 mb-5 ml-6">Base values used by RAMS metric calculations.</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Operating Hours per Day</Label>
              <Input type="number" min={1} max={24} value={settings.op_hours_per_day}
                onChange={(e) => setSettings({...settings, op_hours_per_day: Number(e.target.value)})}
                className="mt-1.5" data-testid="settings-op-hours" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">MTTR Recovery Basis</Label>
              <Select value={settings.mttr_basis} onValueChange={(v) => setSettings({...settings, mttr_basis: v})}>
                <SelectTrigger className="mt-1.5" data-testid="settings-mttr-basis"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="delay">Delay column</SelectItem>
                  <SelectItem value="andil">Andil Kelambatan column</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <h3 className="text-sm font-bold text-slate-700 mt-6 mb-3 uppercase tracking-wider">KPI Targets</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <Label className="text-xs text-slate-600">Target MTBF (Hours)</Label>
              <Input type="number" step="0.1" value={settings.target_mtbf_hours}
                onChange={(e) => setSettings({...settings, target_mtbf_hours: Number(e.target.value)})} className="mt-1.5" data-testid="settings-target-mtbf" />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Target Availability (%)</Label>
              <Input type="number" step="0.01" value={settings.target_availability}
                onChange={(e) => setSettings({...settings, target_availability: Number(e.target.value)})} className="mt-1.5" data-testid="settings-target-avail" />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Target MTTR (Min)</Label>
              <Input type="number" step="0.1" value={settings.target_mttr_minutes}
                onChange={(e) => setSettings({...settings, target_mttr_minutes: Number(e.target.value)})} className="mt-1.5" data-testid="settings-target-mttr" />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Target Safety Score</Label>
              <Input type="number" step="0.01" value={settings.target_safety}
                onChange={(e) => setSettings({...settings, target_safety: Number(e.target.value)})} className="mt-1.5" data-testid="settings-target-safety" />
            </div>
          </div>

          {isAdmin && (
            <div className="mt-6">
              <Button className="bg-[#1e40af] hover:bg-[#1e3a8a]" onClick={() => saveSettings()} data-testid="settings-save">
                <Save className="w-3.5 h-3.5 mr-1.5" /> Save Settings
              </Button>
            </div>
          )}
        </div>
      </div>

      {isAdmin && (
        <div className="bg-white p-6 rounded-xl border border-slate-200/60 kpi-shadow relative overflow-hidden">
          <div className="absolute top-0 left-0 w-1.5 h-full bg-gradient-to-b from-[#22c55e] to-[#84cc16]" />
          <div className="pl-3">
            <div className="flex items-center gap-2">
              <span className="text-[#93c5fd] font-black text-lg">3.</span>
              <h2 className="text-lg font-bold text-[#1e40af] uppercase tracking-tight">User Management</h2>
            </div>
            <p className="text-xs text-slate-500 mt-1 mb-5 ml-6">Create new users and manage roles.</p>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-5 items-end">
              <div>
                <Label className="text-xs">Name</Label>
                <Input value={newUser.name} onChange={(e) => setNewUser({...newUser, name: e.target.value})} placeholder="Full name" data-testid="user-name" />
              </div>
              <div>
                <Label className="text-xs">Email</Label>
                <Input type="email" value={newUser.email} onChange={(e) => setNewUser({...newUser, email: e.target.value})} placeholder="user@trams.local" data-testid="user-email" />
              </div>
              <div>
                <Label className="text-xs">Password</Label>
                <Input type="password" value={newUser.password} onChange={(e) => setNewUser({...newUser, password: e.target.value})} data-testid="user-password" />
              </div>
              <div>
                <Label className="text-xs">Role</Label>
                <Select value={newUser.role} onValueChange={(v) => setNewUser({...newUser, role: v})}>
                  <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="viewer">Viewer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button className="bg-[#1e40af] hover:bg-[#1e3a8a]" onClick={createUser} data-testid="user-create">
                <UserPlus className="w-3.5 h-3.5 mr-1.5" /> Add User
              </Button>
            </div>

            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-slate-500 border-b">
                  <th className="text-left pb-2">Name</th>
                  <th className="text-left pb-2">Email</th>
                  <th className="text-left pb-2">Role</th>
                  <th className="text-right pb-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} className="border-b border-slate-100">
                    <td className="py-2">{u.name}</td>
                    <td className="py-2">{u.email}</td>
                    <td className="py-2 uppercase text-xs font-semibold">{u.role}</td>
                    <td className="py-2 text-right">
                      {u.id !== user?.id && (
                        <button className="text-red-500 hover:text-red-700 p-1.5" onClick={() => delUser(u.id)}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <h3 className="text-sm font-bold text-slate-700 mt-6 mb-3 uppercase tracking-wider">Change Password</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
              <div>
                <Label className="text-xs">User Email</Label>
                <Select value={pwChange.email} onValueChange={(v) => setPwChange({...pwChange, email: v})}>
                  <SelectTrigger><SelectValue placeholder="Select user" /></SelectTrigger>
                  <SelectContent>{users.map(u => <SelectItem key={u.email} value={u.email}>{u.email}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">New Password</Label>
                <Input type="password" value={pwChange.new_password} onChange={(e) => setPwChange({...pwChange, new_password: e.target.value})} />
              </div>
              <Button className="bg-[#1e40af] hover:bg-[#1e3a8a]" onClick={changePw}>
                <Key className="w-3.5 h-3.5 mr-1.5" /> Change Password
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
